import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface GpsCoordinate {
  readonly lat: number;
  readonly lng: number;
  readonly timestamp: number;
  readonly accuracy: number;
}

interface ActiveTrip {
  readonly tripId: string;
  readonly startLocation: string;
  readonly clientId: string | null;
  readonly purpose: string;
  readonly startedAt: string;
  readonly gpsPoints: readonly GpsCoordinate[];
}

interface TripState {
  readonly activeTrip: ActiveTrip | null;
  readonly gpsWatchId: number | null;
  readonly isTracking: boolean;
  readonly gpsPermission: 'prompt' | 'granted' | 'denied' | 'unknown';
}

interface TripActions {
  readonly startTrip: (trip: Omit<ActiveTrip, 'gpsPoints'>) => void;
  readonly stopTrip: () => void;
  readonly addGpsPoint: (point: GpsCoordinate) => void;
  readonly setGpsWatchId: (id: number | null) => void;
  readonly setGpsPermission: (status: TripState['gpsPermission']) => void;
  readonly clearGpsPoints: () => void;
}

type TripStore = TripState & TripActions;

const INITIAL_STATE: TripState = {
  activeTrip: null,
  gpsWatchId: null,
  isTracking: false,
  gpsPermission: 'unknown',
};

export const useTripStore = create<TripStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      startTrip: (trip) =>
        set({
          activeTrip: { ...trip, gpsPoints: [] },
          isTracking: true,
        }),

      stopTrip: () =>
        set((state) => {
          if (state.gpsWatchId !== null) {
            navigator.geolocation.clearWatch(state.gpsWatchId);
          }
          return { ...INITIAL_STATE, gpsPermission: state.gpsPermission };
        }),

      addGpsPoint: (point) =>
        set((state) => {
          if (!state.activeTrip) return state;
          // Keep max 500 points in memory to avoid storage overflow
          const points = state.activeTrip.gpsPoints.length >= 500
            ? [...state.activeTrip.gpsPoints.slice(-499), point]
            : [...state.activeTrip.gpsPoints, point];
          return {
            activeTrip: {
              ...state.activeTrip,
              gpsPoints: points,
            },
          };
        }),

      setGpsWatchId: (id) => set({ gpsWatchId: id }),

      setGpsPermission: (status) => set({ gpsPermission: status }),

      clearGpsPoints: () =>
        set((state) => {
          if (!state.activeTrip) return state;
          return {
            activeTrip: { ...state.activeTrip, gpsPoints: [] },
          };
        }),
    }),
    {
      name: 'trackkm-trip',
      // Only persist activeTrip and gpsPermission, not watchId
      partialize: (state) => ({
        activeTrip: state.activeTrip,
        isTracking: state.isTracking,
        gpsPermission: state.gpsPermission,
      }),
    },
  ),
);
